import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Home, 
  Shield, 
  CheckCircle,
  Phone,
  Award,
  Clock,
  AlertTriangle,
  Flame,
  Droplets,
  Wind,
  FileCheck,
  Users,
  Building2,
  Wrench,
  HardHat
} from "lucide-react";

// Import restoration images
import roofRepairBeforeAfter from "@/assets/roof-repair-before-after-new.jpg";
import commercialRoofRepair from "@/assets/commercial-roof-repair.jpg";
import emergencyRoofRepair from "@/assets/emergency-roof-repair.jpg";
import masonryRepair from "@/assets/masonry-repair-restoration.jpg";
import drywallFinishing from "@/assets/drywall-finishing-professional.jpg";
import emergencyPlumbing from "@/assets/emergency-plumbing.jpg";
import emergencyElectrical from "@/assets/emergency-electrical.jpg";
import emergencyHVAC from "@/assets/emergency-hvac-repair.jpg";

const RestorationServices = () => {
  const faqData = [
    {
      question: "Do you work with insurance companies in Toronto and Niagara?",
      answer: "Yes. Blue Knight Contracting works directly with all major insurance companies throughout Toronto, Hamilton, and Niagara Region. We handle documentation, estimates, and direct billing to streamline your insurance restoration claim."
    },
    {
      question: "How quickly can you respond to emergency restoration needs?",
      answer: "We provide 24/7 emergency response for restoration services across the GTA, Hamilton, and Niagara Region. Our team can typically be on-site within hours for urgent fire, water, or storm damage situations."
    },
    {
      question: "What types of property damage do you restore?",
      answer: "We restore all types of property damage including fire, smoke, water, flood, storm, wind, mold, and structural damage for both residential and commercial properties across Ontario."
    },
    {
      question: "Do you handle both restoration and reconstruction?",
      answer: "Yes. We provide complete restoration services from initial emergency response and mitigation through full reconstruction and rebuilding. Our team manages the entire process from damage assessment to final restoration."
    },
    {
      question: "Are you licensed for insurance restoration work in Ontario?",
      answer: "Absolutely. Blue Knight Contracting is fully licensed, bonded, and insured for restoration work in Ontario. Our team is certified in water damage restoration, fire restoration, and mold remediation."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Insurance Restoration Services Ontario",
    description: "Professional insurance restoration services across Ontario including fire, water, storm damage restoration for residential and commercial properties. Working with all major insurance companies in Toronto, Hamilton, and Niagara Region.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction"
    },
    serviceType: "Restoration Services",
    areaServed: "Ontario, Canada"
  };

  const restorationServices = [
    {
      title: "Fire & Smoke Restoration",
      description: "Complete fire and smoke damage restoration including structural repairs, odor removal, and content cleaning.",
      icon: Flame,
      services: [
        "Fire damage assessment",
        "Smoke and soot removal",
        "Odor elimination",
        "Structural fire repairs",
        "Content restoration",
        "Insurance documentation"
      ]
    },
    {
      title: "Water Damage Restoration",
      description: "Emergency water extraction, drying, dehumidification, and complete water damage restoration services.",
      icon: Droplets,
      services: [
        "Emergency water extraction",
        "Structural drying",
        "Dehumidification services",
        "Mold prevention",
        "Flooring restoration",
        "Plumbing repairs"
      ]
    },
    {
      title: "Storm & Wind Damage",
      description: "Storm damage restoration including roof repairs, siding replacement, and structural repairs.",
      icon: Wind,
      services: [
        "Roof storm damage repair",
        "Siding replacement",
        "Window and door repairs",
        "Structural assessments",
        "Emergency tarping",
        "Tree damage cleanup"
      ]
    },
    {
      title: "Insurance Claims Support",
      description: "Complete insurance claims assistance including documentation, estimates, and direct insurance billing.",
      icon: FileCheck,
      services: [
        "Insurance claim documentation",
        "Damage assessments",
        "Detailed estimates",
        "Direct insurance billing",
        "Claims negotiation support",
        "Project management"
      ]
    },
    {
      title: "Commercial Restoration",
      description: "Large-scale commercial property restoration for offices, retail, warehouses, and industrial facilities.",
      icon: Building2,
      services: [
        "Commercial property assessment",
        "Business continuity planning",
        "Large-scale restoration",
        "Tenant coordination",
        "Code compliance updates",
        "Fast-track restoration"
      ]
    },
    {
      title: "Complete Reconstruction",
      description: "Full property reconstruction services from foundation to finish after severe damage.",
      icon: HardHat,
      services: [
        "Structural reconstruction",
        "Complete rebuilds",
        "Interior finishing",
        "Exterior restoration",
        "Permit management",
        "Final inspections"
      ]
    }
  ];

  const serviceAreas = [
    "Greater Toronto Area",
    "Toronto", "Mississauga", "Brampton", "Vaughan", "Markham", "Richmond Hill", "Oakville", "Burlington", "Milton",
    "Niagara Region", 
    "Niagara Falls", "St. Catharines", "Welland", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Lincoln", "Pelham", "Port Colborne", "Thorold",
    "Hamilton & Area",
    "Hamilton", "Ancaster", "Dundas", "Stoney Creek", "Waterdown",
    "Southwestern Ontario", 
    "London", "Windsor", "Kitchener", "Waterloo", "Cambridge", "Guelph",
    "Eastern Ontario",
    "Ottawa", "Kingston", "Belleville"
  ];

  return (
    <>
      <SEOMeta 
        title="Insurance Restoration Services | Fire, Water & Storm Damage | Toronto, Hamilton, Niagara Region"
        description="Professional insurance restoration services in Ontario. 24/7 emergency response for fire, water, storm damage. Working with all major insurers in Toronto, Hamilton & Niagara."
        keywords="insurance restoration Ontario, fire damage restoration, water damage restoration, storm damage repair, restoration services Toronto, emergency restoration Hamilton, Niagara Region restoration, commercial restoration, residential restoration, insurance claims"
        canonicalUrl="https://blueknightcontracting.ca/services/restoration"
      />
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Insurance Restoration Services in Ontario
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting provides professional insurance restoration services across Toronto, Hamilton, and Niagara Region. We work directly with all major insurance companies to restore your property after fire, water, storm, or other damage.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Assessment</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="bg-white/10 text-white border-white hover:bg-white/20">
                    <Phone className="h-5 w-5 mr-2" />
                    24/7 Emergency
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={roofRepairBeforeAfter} 
                  alt="Professional insurance restoration before and after showing fire and water damage repair in Toronto"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Complete Restoration Solutions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From emergency response to complete reconstruction, we handle every aspect of property restoration across Ontario.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {restorationServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Restoration Projects Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                See our professional restoration work throughout Toronto, Hamilton, and Niagara Region
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={commercialRoofRepair} 
                    alt="Commercial roof restoration after storm damage in Toronto GTA"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Commercial Restoration</h3>
                  <p className="text-muted-foreground">
                    Large-scale commercial property restoration services
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={emergencyRoofRepair} 
                    alt="Emergency roof repair and restoration services in Niagara Region"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Emergency Repairs</h3>
                  <p className="text-muted-foreground">
                    24/7 emergency restoration and damage mitigation
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={masonryRepair} 
                    alt="Masonry restoration and structural repair work in Hamilton Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Structural Restoration</h3>
                  <p className="text-muted-foreground">
                    Complete structural repairs and masonry restoration
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={drywallFinishing} 
                    alt="Interior restoration and drywall finishing after water damage"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Interior Restoration</h3>
                  <p className="text-muted-foreground">
                    Complete interior restoration and finishing services
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Emergency Services CTA */}
        <section className="py-16 bg-destructive text-white">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <AlertTriangle className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Property Damage Emergency? We're Here 24/7
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Fire, flood, or storm damage? Don't wait - call our emergency restoration team for immediate response across Toronto, Hamilton, and Niagara Region.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button size="lg" variant="secondary">
                  <Phone className="h-5 w-5 mr-2" />
                  Call Emergency Line
                </Button>
                <div className="flex items-center gap-2 text-white/90">
                  <Clock className="h-5 w-5" />
                  <span>Available 24/7/365</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Restoration Process */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Our Restoration Process
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                A streamlined approach to get your property restored quickly and properly
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">1. Emergency Contact</h3>
                <p className="text-muted-foreground">
                  24/7 emergency response and immediate dispatch to your property
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileCheck className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">2. Assessment</h3>
                <p className="text-muted-foreground">
                  Complete damage assessment and insurance documentation
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">3. Restoration</h3>
                <p className="text-muted-foreground">
                  Professional restoration work with quality materials
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">4. Final Inspection</h3>
                <p className="text-muted-foreground">
                  Quality assurance and final walkthrough with you
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight Restoration?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Insurance Experts</h3>
                <p className="text-muted-foreground">
                  Direct billing and claims support with all major insurers
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Licensed & Certified</h3>
                <p className="text-muted-foreground">
                  Fully licensed restoration specialists across Ontario
                </p>
              </Card>

              <Card className="text-center p-6">
                <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">24/7 Emergency</h3>
                <p className="text-muted-foreground">
                  Round-the-clock emergency response and support
                </p>
              </Card>

              <Card className="text-center p-6">
                <Users className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Experienced Team</h3>
                <p className="text-muted-foreground">
                  Certified restoration professionals with proven results
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Restoration Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Blue Knight Contracting provides emergency restoration services throughout Ontario, with rapid response in the GTA, Hamilton, and Niagara Region
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => {
                const isRegionHeader = area.includes("Ontario") || area.includes("Region") || area.includes("Area") || area.includes("Hamilton");
                
                return (
                  <Card 
                    key={index} 
                    className={`p-4 text-center ${isRegionHeader ? 'bg-primary text-white col-span-full sm:col-span-2 lg:col-span-4' : ''}`}
                  >
                    {isRegionHeader ? (
                      <h3 className="text-xl font-bold">{area}</h3>
                    ) : (
                      <p className="text-sm">{area}</p>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                  Frequently Asked Questions
                </h2>
              </div>

              <div className="space-y-6">
                {faqData.map((faq, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <Card className="bg-primary text-white p-12 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Need Insurance Restoration Services?
              </h2>
              <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
                Contact Blue Knight Contracting today for a free damage assessment and insurance restoration quote. We're here to help restore your property across Toronto, Hamilton, and Niagara Region.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" variant="secondary">
                  <Link to="/contact">Request Free Assessment</Link>
                </Button>
                <Button size="lg" variant="outline" className="bg-white/10 text-white border-white hover:bg-white/20">
                  <Phone className="h-5 w-5 mr-2" />
                  Call 24/7 Emergency
                </Button>
              </div>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default RestorationServices;